import React, { useState, useMemo, memo, useCallback, useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import useDynamicRecoil from '~/shared/hooks/useDynamicRecoil';
import { map, filter, isEqual } from 'lodash';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useSearchParams } from '~/shared/hooks';
import { IconButton } from '@mui/material';
import { dateValues } from './constants';
import DateFilterRange from './DateFilter/DateFilterRange';
import DateFilterStandAlone from './DateFilter/DateFilterStandAlone';
import DropdownFilter from './DropDownFilter/DropDownFilter';
import CascadingNarrativeFilterBar from './CascadingNarrativeFilterBar';
import { getIsTimeResolution, getTimeResolutionOptions, getQuerystringResolution } from '~/shared/utils';
import { CloudDownload } from '@mui/icons-material';
import { Tooltip } from '~/shared/components';
import type { FilterHierarchyConfig } from '~/shared/utils/filterHierarchy';
import {
  extractFilterHierarchyFromFilters,
  isFilterHierarchyType,
  normalizeFilterHierarchyConfig,
  omitFilterHierarchyFilters,
} from '~/shared/utils/filterHierarchy';

export type NarrativeSelection = { id: string; name: string };

const ToggleButton = styled(IconButton)`
  position: absolute;
  right: 10px;
  top: 10px;
  width: 32px;
  height: 32px;
  color: var(--filter-bar-toggle-button-color);
`;

const DownloadButton = styled(IconButton)`
  margin-top: 30px;
  width: 32px;
  height: 32px;
  color: var(--filter-bar-toggle-button-color);
`;

const FilterTitle = styled(Typography)``;

const FilterGroupTitle = styled(Typography)`
  margin: 35px 8px 8px 0px;
  color: #4c5e72;
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
`;

const keysToComponentMap: { [key: string]: any } = {
  'Drop Down': DropdownFilter,
  'Drop Down-Range': DropdownFilter,
  Calendar: DateFilterStandAlone,
  'calendar-day': DateFilterStandAlone,
  'calendar-month': DateFilterStandAlone,
  'calendar-year': DateFilterStandAlone,
  'Calendar-Range': DateFilterRange,
  'calendar-day-Range': DateFilterRange,
  'calendar-month-Range': DateFilterRange,
  'calendar-year-Range': DateFilterRange,
};

function FilterItem({
  filters,
  dateFilter,
  type,
  dateType,
  name,
  value,
  description,
  tooltipCfg,
  isTimeRange,
  measure,
  isTimeResolution,
  isTimeResolutionLongShort,
  timeResolution,
  multiSelect = false,
  options: providedOptions,
  defaultValue,
  ...rest
}: any) {
  const compType = `${String(type)}${isTimeRange || isTimeResolution ? '-Range' : ''}`;
  const Cmp = keysToComponentMap[compType];
  const searchParams = useSearchParams();
  const setParams = searchParams.setParams;
  const optionsForFilter = useMemo(() => (Array.isArray(providedOptions) ? providedOptions : []), [providedOptions]);

  const narrativeFallbackIds = useMemo(() => {
    if (name !== 'Narrative') {
      return [] as string[];
    }

    const resolveCandidate = (candidate: string | number | null | undefined) => {
      if (candidate === undefined || candidate === null) return null;
      const trimmed = typeof candidate === 'string' ? candidate.trim() : candidate;
      if (trimmed === undefined || trimmed === null || trimmed === '') return null;
      return String(trimmed);
    };

    const fromDefault = Array.isArray(defaultValue)
      ? (defaultValue as Array<string | number>)
          .map(resolveCandidate)
          .filter((candidate): candidate is string => candidate !== null)
      : typeof defaultValue === 'string' && defaultValue.trim()
        ? defaultValue
            .split(',')
            .map(token => resolveCandidate(token))
            .filter((candidate): candidate is string => candidate !== null)
        : [];

    if (fromDefault.length > 0) {
      return fromDefault;
    }

    const firstOption = optionsForFilter[0];
    const resolvedFromFirst = resolveCandidate(
      firstOption?.id ?? firstOption?.value ?? firstOption?.iso ?? firstOption?.name ?? null,
    );
    if (!resolvedFromFirst) {
      return [];
    }

    return [resolvedFromFirst];
  }, [defaultValue, name, optionsForFilter]);

  const isMultiSelect = useMemo(
    () => multiSelect === true || (typeof multiSelect === 'string' && multiSelect === 'true'),
    [multiSelect],
  );
  const [updatedValue, setUpdatedValue] = useState(value);

  useEffect(() => {
    if (isMultiSelect) {
      const nextValue = Array.isArray(value)
        ? value
        : typeof value === 'string' && value
          ? value
              .split(',')
              .map((item: string) => item.trim())
              .filter(Boolean)
          : [];
      const currentValue = Array.isArray(updatedValue) ? updatedValue : [];
      if (!isEqual(nextValue, currentValue)) {
        setUpdatedValue(nextValue);
      }
      return;
    }
    if ((name === 'Asset' || name === 'Narrative') && value !== updatedValue) {
      setUpdatedValue(value);
    }
  }, [isMultiSelect, name, updatedValue, value]);

  const onChange = useCallback(
    (nextValue: string | string[]) => {
      if (isMultiSelect) {
        const normalized = Array.isArray(nextValue)
          ? nextValue
          : typeof nextValue === 'string' && nextValue
            ? nextValue
                .split(',')
                .map(item => item.trim())
                .filter(Boolean)
            : [];
        if (name === 'Narrative' && normalized.length === 0 && narrativeFallbackIds.length > 0) {
          setParams({ newParams: { [name]: narrativeFallbackIds.join(',') } });
          setUpdatedValue(narrativeFallbackIds);
          return;
        }
        setParams({ newParams: { [name]: normalized.join(',') } });
        setUpdatedValue(normalized);
        return;
      }
      if (
        name === 'Measure' &&
        !!filters?.find((f: any) => f.id === 'Measure')?.options?.find((o: any) => o.id === 'Abnormal Intensity') &&
        nextValue !== 'Abnormal Intensity'
      ) {
        if (filters?.find((f: any) => f.id === 'Time Resolution')) {
          setParams({
            newParams: { [name]: nextValue },
            newExtraParams: { 'Time Resolution': filters?.find((f: any) => f.id === 'Time Resolution')?.defaultValue },
          });
          setUpdatedValue(nextValue);
        } else {
          setParams({ newParams: { [name]: nextValue } });
          setUpdatedValue(nextValue);
        }
      } else {
        setParams({ newParams: { [name]: nextValue } });
        setUpdatedValue(nextValue);
      }
    },
    [filters, isMultiSelect, name, narrativeFallbackIds, setParams],
  );

  const onChangeTime = useCallback((value: any, measure: string) => {
    setParams({ newParams: { [name]: value }, newExtraParams: { Measure: measure } });
    setUpdatedValue(value);
  }, []);

  const location = useLocation();

  const onChangeTimeResolution = useCallback(
    (value: any, timeResolution: string) => {
      if (value === undefined) return;
      if (filters?.find((f: any) => f.id === 'Time Resolution')) {
        setParams({
          newParams: { [name]: value },
          newExtraParams: { 'Time Resolution': timeResolution },
        });
      }
      if (filters?.find((f: any) => f.id === 'Formation Period'))
        setParams({
          newParams: { [name]: value },
          newExtraParams: {
            'Formation Period': timeResolution,
          },
        });
      setUpdatedValue(value);
    },
    [name, value, location],
  );

  const resolvedValue = useMemo(() => {
    if (isMultiSelect) {
      if (Array.isArray(updatedValue)) return updatedValue;
      if (typeof updatedValue === 'string' && updatedValue) {
        return updatedValue
          .split(',')
          .map((item: string) => item.trim())
          .filter(Boolean);
      }
      return [];
    }
    if (Array.isArray(updatedValue)) {
      return updatedValue[0] ?? '';
    }
    return updatedValue ?? '';
  }, [isMultiSelect, updatedValue]);

  if (isFilterHierarchyType(type) || !Cmp) {
    return null;
  }

  return (
    <Cmp
      {...{
        ...rest,
        name,
        value: resolvedValue,
        onChange,
        onChangeTime: !isTimeResolution && onChangeTime,
        onChangeTimeResolution: isTimeResolution && onChangeTimeResolution,
        type,
        dateType,
        description,
        tooltipCfg,
        measure,
        timeResolution,
        filters,
        dateFilter,
        isTimeResolution,
        isTimeResolutionLongShort,
        options: providedOptions,
        defaultValue,
        multiSelect: isMultiSelect,
      }}
    />
  );
}

const FilterPaper = styled<any>(Paper)`
  && {
    position: relative;
    overflow: visible;

    display: flex;
    flex-direction: row;

    min-height: ${({ $isheatmap }) => ($isheatmap ? '100%' : 'unset')};
    box-shadow: none;
    background: transparent;
    width: 100%;
    min-height: 93px;
    transition: all 600ms;

    & > :not(${ToggleButton}) {
      opacity: ${({ open }) => (open ? 1 : 0)};
      transition: opacity ${({ open }) => (open ? '400ms ease 400ms' : 0)};
    }

    & > ${DownloadButton} {
      opacity: ${({ open }) => (open ? 1 : 0)};
      transition: opacity ${({ open }) => (open ? '400ms ease 400ms' : 0)};
    }

    .button-primary {
      color: white;
      font-size: 14px;
      font-weight: 400;
      border: 1px solid var(--filter-bar-primary-button-border) !important;
      border-radius: 4px;
      background: var(--filter-bar-primary-button-background) !important;
      width: 98px;
      padding: 7px 15px 6px;
      margin: 16px 6px;
      text-transform: unset !important;
    }

    .button-primary:hover {
      color: white;
      font-size: 14px;
      font-weight: 400;
      border: 1px solid var(--filter-bar-primary-button-border) !important;
      border-radius: 4px;
      background: var(--filter-bar-primary-button-background) !important;
      width: 98px;
      padding: 7px 15px 6px;
      margin: 16px 6px;
      text-transform: unset !important;
    }
  }
`;
const FilterBarContainer = styled('div')`
  display: flex;
`;

const getDefaultResolution = (filters: any) => {
  if (filters?.find((f: any) => f.name === 'Formation Period')) {
    return filters.find((f: any) => f.name === 'Formation Period')?.defaultValue;
  }
  if (filters?.find((f: any) => f.name === 'Time Resolution')) {
    return filters.find((f: any) => f.name === 'Time Resolution')?.defaultValue;
  }
  if (filters?.find((f: any) => f.id === 'Time Resolution')) {
    return filters.find((f: any) => f.id === 'Time Resolution')?.defaultValue;
  }
};

function FilterBar({
  dateType = 'calendar',
  filters,
  values,
  onDownload,
  tooltipCfg,
  isHeatmap = false,
  isGrid = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setParentOpen = (val: any) => val,
  prismId,
  showDownloadAllButton = false,
  onNarrativeSelectionChange,
  filterHierarchy,
  filterHierarchyValues,
}: any) {
  const location = useLocation();
  const search = location?.search;
  const { setParams } = useSearchParams();

  const querystringResolution = getQuerystringResolution(search);

  const urlResolution = (() => {
    if (!querystringResolution) return undefined;
    try {
      const decoded = decodeURIComponent(querystringResolution.replace(/\+/g, ' '));
      return decoded.includes('Day') ? decoded : undefined;
    } catch (error) {
      return undefined;
    }
  })();
  const [open] = useState(true);

  const resolutionOptions = useMemo(() => getTimeResolutionOptions({ filters }), [filters]);

  const defaultResolution = useMemo(() => getDefaultResolution(filters), [filters]);

  const initialResolution = useMemo(() => {
    if (urlResolution) return urlResolution;
    if (defaultResolution) return defaultResolution;
    const persisted = localStorage.getItem('dateFilterResolution');
    if (persisted) return persisted;
    return resolutionOptions?.[0]?.id || '7-Day';
  }, [urlResolution, defaultResolution, resolutionOptions]);

  const [selectedResolution, setSelectedResolution] = useDynamicRecoil('dateFilterResolution', initialResolution);

  useEffect(() => {
    if (!selectedResolution) {
      const fallbackResolution = urlResolution || defaultResolution || resolutionOptions?.[0]?.id || '7-Day';
      setSelectedResolution(fallbackResolution);
    }
  }, [selectedResolution, urlResolution, defaultResolution, resolutionOptions, setSelectedResolution]);

  const isTimeRange = false;

  const isTimeResolution = getIsTimeResolution(dateValues, values);

  const isTimeResolutionLongShort = useMemo(
    () =>
      !dateValues.includes(values?.['Measure']) &&
      !!values?.['Time Resolution'] &&
      !values?.['Time Resolution']?.includes('-'),
    [dateValues, values],
  );

  const isAbnormalIntensity = values?.['Measure'] === 'Abnormal Intensity';
  const isBreakdownBy = values && 'Breakdown By' in values;
  const isLifeCycle = values && 'Asset' in values;

  const resolvedFilterHierarchy = useMemo(() => {
    const raw = filterHierarchy ?? extractFilterHierarchyFromFilters(filters) ?? null;
    if (!raw) return null;
    return normalizeFilterHierarchyConfig(raw) ?? (raw as FilterHierarchyConfig);
  }, [filterHierarchy, filters]);

  const filtersWithoutHierarchy = useMemo(() => omitFilterHierarchyFilters(filters) ?? filters ?? [], [filters]);

  const tempFilters = map(filtersWithoutHierarchy, currentFilter => {
    const multiSelectRaw = currentFilter?.multiSelect ?? currentFilter?.['multi-select'];
    const isMultiSelectFilter = multiSelectRaw === true || multiSelectRaw === 'true';
    const baseFilter = {
      ...currentFilter,
      multiSelect: isMultiSelectFilter,
    };

    if (
      (currentFilter.id === 'Time Resolution' || currentFilter.name === 'Time Resolution') &&
      !isAbnormalIntensity &&
      !isBreakdownBy &&
      !isLifeCycle
    ) {
      const normalizedDefault = urlResolution || defaultResolution || resolutionOptions?.[0]?.id || '7-Day';
      return {
        ...baseFilter,
        'default-value': normalizedDefault,
        defaultValue: normalizedDefault,
        options: filter(
          currentFilter.options,
          ({ id }) => (id === urlResolution && (defaultResolution || resolutionOptions?.[0]?.id)) || '7-Day',
        ),
      };
    }

    return baseFilter;
  });
  const tempDateFilter = tempFilters.pop();

  const narrativeSelectionData = useMemo(() => {
    const narrativeFilter = filters?.find((f: any) => f?.name === 'Narrative');
    if (!narrativeFilter) {
      return {
        selections: [] as NarrativeSelection[],
        normalizedIds: [] as string[],
        source: 'none' as 'none' | 'explicit' | 'fallback',
        filterName: undefined as string | undefined,
      };
    }

    const multiSelectRaw = narrativeFilter?.multiSelect ?? narrativeFilter?.['multi-select'];
    const isNarrativeMultiSelect = multiSelectRaw === true || multiSelectRaw === 'true';
    if (!isNarrativeMultiSelect) {
      return {
        selections: [] as NarrativeSelection[],
        normalizedIds: [] as string[],
        source: 'none' as 'none' | 'explicit' | 'fallback',
        filterName: narrativeFilter?.name,
      };
    }

    const optionsForFilter = narrativeFilter?.options ?? [];

    const resolveTokenToId = (token: string | number | null | undefined) => {
      const trimmed = typeof token === 'string' ? token.trim() : token;
      if (trimmed === undefined || trimmed === null || trimmed === '') {
        return null;
      }
      const asString = String(trimmed);
      const matchedOption = optionsForFilter.find((option: any) => {
        const candidates = [option?.id, option?.value, option?.iso, option?.name]
          .filter(candidate => candidate !== undefined && candidate !== null)
          .map((candidate: any) => String(candidate));
        return candidates.includes(asString);
      });
      return matchedOption?.id ?? trimmed;
    };

    const splitToIds = (value: string | undefined) =>
      (value || '')
        .split(',')
        .map(resolveTokenToId)
        .filter((token): token is string | number => token !== null) as Array<string | number>;

    const fallbackIdsFromDefault = Array.isArray(narrativeFilter?.defaultValue)
      ? (narrativeFilter?.defaultValue as Array<string | number>)
          .map(resolveTokenToId)
          .filter((token): token is string | number => token !== null)
      : typeof narrativeFilter?.defaultValue === 'string' && narrativeFilter?.defaultValue.trim()
        ? splitToIds(narrativeFilter?.defaultValue)
        : [];

    const fallbackIdsFromOptions = (() => {
      const firstOption = optionsForFilter[0];
      if (!firstOption) return [] as Array<string | number>;
      const resolved = resolveTokenToId(firstOption?.id ?? firstOption?.value ?? firstOption?.name ?? null);
      return resolved !== null ? [resolved] : ([] as Array<string | number>);
    })();

    const fallbackIds = fallbackIdsFromDefault.length > 0 ? fallbackIdsFromDefault : fallbackIdsFromOptions;

    const rawValue = values?.[narrativeFilter?.name];
    const selectedIds = Array.isArray(rawValue)
      ? (rawValue as Array<string | number>)
          .map(resolveTokenToId)
          .filter((token): token is string | number => token !== null)
      : typeof rawValue === 'string' && rawValue.trim()
        ? splitToIds(rawValue)
        : [];

    const hasExplicitSelection = selectedIds.length > 0;
    const normalizedIds = (hasExplicitSelection ? selectedIds : fallbackIds).map(id => String(id));

    const selections = normalizedIds.map(id => {
      const matchedOption = optionsForFilter.find((option: any) => {
        const candidates = [option?.id, option?.value, option?.iso, option?.name]
          .filter(candidate => candidate !== undefined && candidate !== null)
          .map((candidate: any) => String(candidate));
        return candidates.includes(String(id));
      });
      return {
        id: String(id),
        name: matchedOption?.name ?? String(id),
      };
    });

    return {
      selections,
      normalizedIds,
      source: hasExplicitSelection
        ? ('explicit' as const)
        : normalizedIds.length > 0
          ? ('fallback' as const)
          : ('none' as const),
      filterName: narrativeFilter?.name,
    };
  }, [filters, values]);

  useLayoutEffect(() => {
    if (typeof onNarrativeSelectionChange === 'function') {
      onNarrativeSelectionChange(narrativeSelectionData.selections);
    }

    if (
      narrativeSelectionData.source === 'fallback' &&
      narrativeSelectionData.filterName &&
      narrativeSelectionData.normalizedIds.length > 0
    ) {
      const paramValueRaw = values?.[narrativeSelectionData.filterName];
      const normalizedParam = Array.isArray(paramValueRaw)
        ? (paramValueRaw as Array<string | number>).map(token => String(token)).join(',')
        : typeof paramValueRaw === 'string'
          ? paramValueRaw
          : '';
      const targetValue = narrativeSelectionData.normalizedIds.join(',');
      if (normalizedParam !== targetValue) {
        setParams({ newParams: { [narrativeSelectionData.filterName]: targetValue } });
      }
    }
  }, [narrativeSelectionData, onNarrativeSelectionChange, setParams, values]);

  return (
    <FilterPaper
      // Grid dashboard header must stay visible; only embedded widget bars (isGrid) collapse.
      // Hierarchy filters always stay visible so cascading dropdowns are not opacity:0.
      open={(!!resolvedFilterHierarchy || !isGrid) && open}
      $isheatmap={isHeatmap ? 1 : 0}
    >
      {
        <FilterBarContainer>
          {tempDateFilter &&
            !isFilterHierarchyType(tempDateFilter?.type) &&
            [tempDateFilter].map((filter: any) => {
              const resolvedValue =
                filtersWithoutHierarchy
                  ?.filter((f: any) => f?.name === filter?.name)[0]
                  ?.options?.filter((fo: any) => fo?.id === values?.[filter?.name])[0]?.name ||
                (values?.[filter?.name] === resolutionOptions?.[0]?.id || '7-Day'
                  ? filter.defaultValue
                  : values?.[filter?.name]);
              const value = filter.name === 'Formation Period' && urlResolution ? urlResolution : resolvedValue;
              return (
                <FilterItem
                  filters={tempFilters}
                  dateFilter={tempDateFilter}
                  key={filter.name}
                  type={filter.type}
                  dateType={dateType}
                  value={value}
                  tooltipCfg={tooltipCfg}
                  isTimeRange={isTimeRange}
                  measure={values?.['Measure']}
                  isTimeResolution={isTimeResolution}
                  isTimeResolutionLongShort={isTimeResolutionLongShort}
                  timeResolution={selectedResolution}
                  prismId={prismId}
                  {...filter}
                />
              );
            })}
          {(resolvedFilterHierarchy ||
            tempFilters.length > 1 ||
            (tempFilters.length === 1 &&
              tempFilters[0].name !== 'Formation Period' &&
              tempFilters[0].defaultValue !== dateValues[0] &&
              !dateValues.includes(tempFilters[0].defaultValue) &&
              tempFilters[0].value !== dateValues[0])) && <FilterGroupTitle></FilterGroupTitle>}
          {resolvedFilterHierarchy && (
            <CascadingNarrativeFilterBar
              tree={resolvedFilterHierarchy as FilterHierarchyConfig}
              values={filterHierarchyValues ?? values}
              tooltipCfg={tooltipCfg}
            />
          )}
          {tempFilters.map((filter: any) => {
            if (isFilterHierarchyType(filter?.type)) {
              return null;
            }
            const optionsForFilter =
              filtersWithoutHierarchy?.find((f: any) => f?.name === filter?.name)?.options ?? filter?.options ?? [];

            const resolveTokenToId = (token: string) => {
              const trimmed = token?.trim();
              if (!trimmed) return null;
              const matchedOption = optionsForFilter.find(
                (option: any) =>
                  option?.id === trimmed ||
                  option?.value === trimmed ||
                  option?.iso === trimmed ||
                  option?.name === trimmed,
              );
              return matchedOption?.id ?? trimmed;
            };

            const splitToIds = (value: string | undefined) =>
              (value || '').split(',').map(resolveTokenToId).filter(Boolean) as string[];

            const filterValue = filter?.multiSelect
              ? (() => {
                  if (Array.isArray(values?.[filter?.name])) {
                    return (values?.[filter?.name] as string[]).map(resolveTokenToId).filter(Boolean);
                  }
                  const rawValue =
                    typeof values?.[filter?.name] === 'string' ? (values?.[filter?.name] as string) : undefined;
                  const fallbackIds = Array.isArray(filter?.defaultValue)
                    ? (filter.defaultValue as string[]).map(resolveTokenToId).filter(Boolean)
                    : typeof filter?.defaultValue === 'string' && filter?.defaultValue
                      ? splitToIds(filter?.defaultValue)
                      : [];
                  const resolvedIds = rawValue ? splitToIds(rawValue) : fallbackIds;
                  return resolvedIds.length > 0 ? resolvedIds : fallbackIds;
                })()
              : values?.[filter?.name] ||
                optionsForFilter?.find((fo: any) => fo?.id === values?.[filter?.name])?.name ||
                (values?.[filter?.name] === resolutionOptions?.[0]?.id || '7-Day'
                  ? filter.defaultValue
                  : values?.[filter?.name]);

            return (
              <FilterItem
                filters={tempFilters}
                key={filter.name}
                value={filterValue}
                tooltipCfg={tooltipCfg}
                isTimeRange={isTimeRange}
                isTimeResolution={isTimeResolution}
                isTimeResolutionLongShort={isTimeResolutionLongShort}
                timeResolution={selectedResolution}
                {...filter}
              />
            );
          })}
          <FilterTitle />

          {showDownloadAllButton && (
            <DownloadButton onClick={onDownload}>
              <Tooltip arrow placement="bottom-start" html="Download all data">
                <CloudDownload color="action" />
              </Tooltip>
            </DownloadButton>
          )}
        </FilterBarContainer>
      }
    </FilterPaper>
  );
}

export default memo(FilterBar);
